<template>
  <!-- headless listener -->
  <div style="display:none;"></div>
</template>

<script>
import { onMounted, onBeforeUnmount } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { GlobalEventEmitter } from '@/utils/GlobalEventEmitter'
import { storage } from '@/utils/storage'
import store from '@/store'
import ability from '@/libs/acl/ability'
import {clearUserData} from '@/views/apps/user/clearUserData'
// long enough for toast to be seen
const REDIRECT_DELAY_MS = 1200

const LOGIN_ROUTE_NAME = 'auth'
const LOGIN_PATH = '/Auth'

// align with serviceQueryHandler contract (we only READ this here)
const REDIRECT_AFTER_LOGIN_KEY = 'redirect_to_after_login'

// single-flight guard
let handling = false

export default {
  name: 'AuthSessionListener',
  setup() {
    const router = useRouter()

    /** ---------- helpers ---------- */
    const getRouteLike = () => {
      const cr = router && (router.currentRoute || (router.app && router.app.$route))
      return cr && cr.value ? cr.value : cr || null
    }

    const getSafeFullPath = () => {
      const r = getRouteLike()
      if (r && (r.fullPath || r.path)) return r.fullPath || r.path
      const { pathname, search, hash } = window.location
      return `${pathname || '/'}${search || ''}${hash || ''}`
    }

    const isOnLogin = () => {
      const r = getRouteLike()
      const name = r && r.name
      if (name === LOGIN_ROUTE_NAME || name === 'login') return true
      return (window.location && window.location.pathname) === LOGIN_PATH
    }

    const resolveReturnTarget = () => {
      try {
        const existing = storage.getItem(REDIRECT_AFTER_LOGIN_KEY)
        if (existing) return existing
      } catch (_) {}
      return getSafeFullPath()
    }

    const showSessionToast = () => {
      const message = i18n.t('auth.sessionExpired') || 'Your session expired. Please sign in again'
      GlobalEventEmitter.$emit(
          'showCustomToast',
          { status: 'error', statusText: message },
          'You are not authorized',
          null,
          null,
          4000,
      )
    }

    // Router can request a one-time toast (tokens removed but hadSession)
    const maybeShowToastOnce = () => {
      try {
        const once = sessionStorage.getItem('auth.toastOnce') === '1'
        if (!once) return

        sessionStorage.removeItem('auth.toastOnce')
        sessionStorage.removeItem('auth.toastReason') // optional detail

        showSessionToast()
      } catch (_) {}
    }

    const safeRedirectToLogin = returnTo => {
      const lang = i18n?.locale
      const query = {
        returnTo,
        form: 'login',
        ...(lang ? { lang } : {}),
      }

      if (router && typeof router.replace === 'function') {
        return router
            .replace({ name: LOGIN_ROUTE_NAME, query })
            .catch(() => router.replace({ path: LOGIN_PATH, query }))
      }

      const qs = new URLSearchParams()
      qs.set('returnTo', returnTo)
      qs.set('form', 'login')
      if (lang) qs.set('lang', lang)
      window.location.href = `${LOGIN_PATH}?${qs.toString()}`
      return Promise.resolve()
    }

    /** ---------- main handler (real expiry only) ---------- */
    const onExpired = () => {
      if (handling) return
      handling = true

      clearUserData()

      // toast BEFORE navigation
      showSessionToast()

      // If already on auth page, stop (avoid loops)
      if (isOnLogin()) {
        setTimeout(() => { handling = false }, 300)
        return
      }

      const returnTo = resolveReturnTarget()
      try { sessionStorage.setItem('auth.returnTo', returnTo) } catch (_) {}

      setTimeout(() => {
        safeRedirectToLogin(returnTo)
            .finally(() => setTimeout(() => { handling = false }, 300))
      }, REDIRECT_DELAY_MS)
    }

    onMounted(() => {
      // show one-time toast if router requested it
      maybeShowToastOnce()
      window.addEventListener('auth:session-expired', onExpired)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('auth:session-expired', onExpired)
    })

    return {}
  },
}
</script>
